import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { OpsMap, MapMarker, TransportLines, useOpsMap, useMarkerLayout, useClusters, useFitTo } from "./OpsMap";
import { ProjectMarker, DepotMarker, TransportChip, ClusterMarker } from "./MapMarkers";
import { LayerToggle, MapAttribution, MapLegend } from "./MapChrome";
import { layerById } from "./mapLayers";
import { boundsOf, arcMidpoint } from "./mapUtils";

const LEGEND = [
  { label: "Prosjekt", dot: "bg-card ring-2 ring-emerald-500" },
  { label: "Depot", dot: "bg-navy" },
  { label: "Transport", dot: "bg-primary" },
];

/** Marker layer for the portfolio payload (/api/map/overview). Lives inside <OpsMap>. */
const PortfolioMarkers = ({ locations, transports, selectedId, onSelect, showTransports, clusterBelowZoom }) => {
  const { zoom } = useOpsMap();
  const fitTo = useFitTo();
  const showLabels = zoom >= 7.2;
  // several legs on the same from→to pair share one chip (7 + 16 + 27 moduler → «50 · 3 turer»)
  const routes = useMemo(() => {
    const byKey = new Map();
    for (const t of transports) {
      if (t.from?.lat == null || t.to?.lat == null) continue;
      const key = `${t.from.id}>${t.to.id}`;
      const r = byKey.get(key) || { key, id: key, from: t.from, to: t.to, project_id: t.project_id, legs: [], module_count: 0, in_transit: false, date: null, transport_number: t.transport_number };
      r.legs.push(t);
      r.module_count += t.module_count || 0;
      r.in_transit = r.in_transit || !!t.in_transit;
      r.date = !r.date || (t.date && t.date < r.date) ? t.date : r.date;
      byKey.set(key, r);
    }
    return [...byKey.values()].map((r) => ({ ...r, mid: arcMidpoint(r.from, r.to) }));
  }, [transports]);
  // zoomed out: geographic clusters; zoomed in: real sites, decluttered
  const { clusters, singles, layout } = useClusters(locations, { radiusPx: 46, clusterBelowZoom });
  // a selected location always shows as itself, even inside a cluster
  const selectedInCluster = selectedId && clusters.find((c) => c.items.some((i) => i.id === selectedId));
  const routePoints = useMemo(() => routes.map((r) => ({ id: r.key, lat: r.mid.lat, lng: r.mid.lng })), [routes]);
  const routeLayout = useMarkerLayout(routePoints, 30);
  return (
    <>
      {showTransports && <TransportLines transports={transports} selectedId={selectedId} />}
      {clusters.map((c) => {
        if (selectedInCluster && selectedInCluster.id === c.id) return null;
        return (
          <MapMarker key={c.id} lat={c.lat} lng={c.lng} zIndex={12}>
            <ClusterMarker cluster={c} onClick={() => fitTo(c.items)} />
          </MapMarker>
        );
      })}
      {(selectedInCluster ? [...singles, ...selectedInCluster.items] : singles).map((loc) => {
        const l = layout[loc.id] || { offset: [0, 0], clusterSize: 1 };
        const selected = selectedId === loc.id;
        const common = { lat: loc.lat, lng: loc.lng, offset: l.offset, zIndex: selected ? 20 : loc.kind === "depot" ? 5 : 10 };
        return (
          <MapMarker key={loc.id} {...common}>
            {loc.kind === "depot" ? (
              <DepotMarker loc={loc} selected={selected} showLabel={showLabels && l.clusterSize === 1} onClick={() => onSelect?.(selected ? null : loc.id)} />
            ) : (
              <ProjectMarker loc={loc} selected={selected} showLabel={showLabels && l.clusterSize === 1} onClick={() => onSelect?.(selected ? null : loc.id)} />
            )}
          </MapMarker>
        );
      })}
      {showTransports &&
        zoom >= 7.2 &&
        routes.map((r) => {
          const targetLoc = locations.find((x) => x.id === r.to.id || x.project?.id === r.project_id);
          const l = routeLayout[r.key] || { offset: [0, 0] };
          return (
            <MapMarker key={r.key} lat={r.mid.lat} lng={r.mid.lng} offset={l.offset} zIndex={r.in_transit ? 15 : 8}>
              <TransportChip t={r} selected={!!selectedId && (selectedId === targetLoc?.id || r.legs.some((l) => l.id === selectedId))} onClick={() => targetLoc && onSelect?.(targetLoc.id)} />
            </MapMarker>
          );
        })}
    </>
  );
};

/**
 * The portfolio map used on Oversikt and Kart: locations + transports from /api/map/overview, chrome (layer toggle,
 * legend, attribution) and any extra overlays passed as children (preview card, date banner...).
 */
export const PortfolioMap = ({ data, visibleIds, selectedId, onSelect, layer, onLayerChange, showTransports = true, className, children, chrome = true, legend = true, topLeft, fitPadding, maxZoom = 10.5, interactive = true, clusterBelowZoom = 9, testId = "portfolio-map" }) => {
  const locations = useMemo(() => {
    const all = data?.locations || [];
    return visibleIds ? all.filter((l) => visibleIds.has(l.id)) : all;
  }, [data, visibleIds]);
  const transports = useMemo(() => (showTransports ? data?.transports || [] : []), [data, showTransports]);
  const bounds = useMemo(() => {
    const pts = [...locations];
    for (const t of transports) {
      if (t.from) pts.push(t.from);
      if (t.to) pts.push(t.to);
    }
    return boundsOf(pts);
  }, [locations, transports]);

  return (
    <OpsMap layer={layer} bounds={bounds} fitPadding={fitPadding} maxZoom={maxZoom} onBackgroundClick={() => onSelect?.(null)} interactive={interactive} className={cn("h-full w-full", className)} testId={testId}>
      <PortfolioMarkers locations={locations} transports={transports} selectedId={selectedId} onSelect={onSelect} showTransports={showTransports} clusterBelowZoom={clusterBelowZoom} />
      {chrome && (
        <>
          <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-wrap items-center gap-2">
            {onLayerChange && <LayerToggle value={layer} onChange={onLayerChange} />}
            {topLeft}
          </div>
          {legend && <MapLegend className="pointer-events-none absolute right-3 top-3 z-10 hidden sm:flex" items={LEGEND} />}
          <MapAttribution layer={layerById(layer)} className="absolute bottom-2 right-2 z-10" />
        </>
      )}
      {children}
    </OpsMap>
  );
};
